/**
 * The AI gradient design tokens.
 *
 * They are kept as SVG, exactly as Figma exports them, because their radial gradients are rotated
 * ellipses: the CSS `radial-gradient()` function only supports axis-aligned ones, so Figma's own
 * CSS export silently drops the rotation. To update a token, re-export the shape from Figma and
 * replace the `<defs>` below.
 */

/** The color the gradients are painted on top of. */
export const AI_GRADIENT_BASE_COLOR = "#05A8AA";

const AI_GRADIENT_SVG = `<svg xmlns="http://www.w3.org/2000/svg" width="256" height="96" viewBox="0 0 80 30" preserveAspectRatio="none">
  <defs>
    <radialGradient id="blue" cx="0" cy="0" r="1" gradientUnits="userSpaceOnUse" gradientTransform="matrix(30.1538 -21.1364 15.7717 27.6208 55.6274 31.8752)">
      <stop stop-color="#3354DB"/>
      <stop offset="0.5" stop-color="#3354DB"/>
      <stop offset="1" stop-color="#3354DB" stop-opacity="0"/>
    </radialGradient>
    <radialGradient id="indigo" cx="0" cy="0" r="1" gradientUnits="userSpaceOnUse" gradientTransform="matrix(26.4615 -4.60244 1.21922 8.60501 48.0889 19.6024)">
      <stop stop-color="#263899"/>
      <stop offset="0.45" stop-color="#263899" stop-opacity="0.5"/>
      <stop offset="1" stop-color="#263899" stop-opacity="0"/>
    </radialGradient>
    <radialGradient id="amber" cx="0" cy="0" r="1" gradientUnits="userSpaceOnUse" gradientTransform="matrix(-4.0649 45.5114 -26.9549 -2.95537 6.41257e-07 3.06835)">
      <stop stop-color="#E87A33"/>
      <stop offset="0.442308" stop-color="#E87A33"/>
      <stop offset="1" stop-color="#E87A33" stop-opacity="0"/>
    </radialGradient>
    <radialGradient id="orange" cx="0" cy="0" r="1" gradientUnits="userSpaceOnUse" gradientTransform="matrix(12.2428 8.01136 -3.9861 7.47767 2.25491e-07 5.45471)">
      <stop stop-color="#FF4500"/>
      <stop offset="0.5" stop-color="#FF4500"/>
      <stop offset="1" stop-color="#FF4500" stop-opacity="0"/>
    </radialGradient>
  </defs>
  <rect width="100%" height="100%" fill="${AI_GRADIENT_BASE_COLOR}"/>
  <rect width="100%" height="100%" fill="url(#blue)"/>
  <rect width="100%" height="100%" fill="url(#indigo)"/>
  <rect width="100%" height="100%" fill="url(#amber)"/>
  <rect width="100%" height="100%" fill="url(#orange)"/>
</svg>`;

const AI_GRADIENT_SMALL_SVG = `<svg xmlns="http://www.w3.org/2000/svg" width="256" height="256" viewBox="0 0 30 30" preserveAspectRatio="none">
  <defs>
    <radialGradient id="blue" cx="0" cy="0" r="1" gradientUnits="userSpaceOnUse" gradientTransform="translate(25.5682 30) rotate(-66.4477) scale(21.7555 22.7411)">
      <stop stop-color="#3354DB"/>
      <stop offset="0.5" stop-color="#3354DB"/>
      <stop offset="1" stop-color="#3354DB" stop-opacity="0"/>
    </radialGradient>
    <radialGradient id="indigo" cx="0" cy="0" r="1" gradientUnits="userSpaceOnUse" gradientTransform="matrix(12.6136 -3.92045 -0.830039 10.3726 15 17.3864)">
      <stop stop-color="#263899"/>
      <stop offset="0.45" stop-color="#263899" stop-opacity="0.5"/>
      <stop offset="1" stop-color="#263899" stop-opacity="0"/>
    </radialGradient>
    <radialGradient id="amber" cx="0" cy="0" r="1" gradientUnits="userSpaceOnUse" gradientTransform="translate(4.43182 4.60227) rotate(117.57) scale(21.7288 10.2195)">
      <stop stop-color="#E87A33"/>
      <stop offset="0.442308" stop-color="#E87A33"/>
      <stop offset="1" stop-color="#E87A33" stop-opacity="0"/>
    </radialGradient>
    <radialGradient id="orange" cx="0" cy="0" r="1" gradientUnits="userSpaceOnUse" gradientTransform="matrix(10.5682 5.28409 3.70506 9.02718 -0.852273 -1.48529e-07)">
      <stop stop-color="#FF4500"/>
      <stop offset="0.5" stop-color="#FF4500"/>
      <stop offset="1" stop-color="#FF4500" stop-opacity="0"/>
    </radialGradient>
  </defs>
  <rect width="100%" height="100%" fill="${AI_GRADIENT_BASE_COLOR}"/>
  <rect width="100%" height="100%" fill="url(#blue)"/>
  <rect width="100%" height="100%" fill="url(#indigo)"/>
  <rect width="100%" height="100%" fill="url(#amber)"/>
  <rect width="100%" height="100%" fill="url(#orange)"/>
</svg>`;

/**
 * @param svg One of the gradient tokens.
 * @returns the token as an image, usable as a CSS background or as a texture source.
 */
function toImageUrl(svg: string): string {
  return `data:image/svg+xml,${encodeURIComponent(svg)}`;
}

/** The sx properties painting a gradient token over an element of any size. */
type AiGradientBackground = {
  backgroundImage: string;
  backgroundSize: string;
  backgroundRepeat: string;
};

/**
 * @param svg One of the gradient tokens.
 * @returns the sx properties painting it as the background of an element.
 */
function toBackground(svg: string): AiGradientBackground {
  return {
    backgroundImage: `url("${toImageUrl(svg)}")`,
    backgroundSize: "100% 100%",
    backgroundRepeat: "no-repeat",
  };
}

/** The "AI gradient" token, for big filled backgrounds. */
export const AI_GRADIENT = toBackground(AI_GRADIENT_SVG);

/** The "AI gradient small" token, for outlines and smaller button backgrounds. */
export const AI_GRADIENT_SMALL = toBackground(AI_GRADIENT_SMALL_SVG);

/** The "AI gradient small" token as an image, for the 3D renderers that sample it as a texture. */
export const AI_GRADIENT_SMALL_IMAGE_URL = toImageUrl(AI_GRADIENT_SMALL_SVG);
